import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2, User } from "lucide-react";
import BlogContent from "../../../components/BlogContent";


const backendUrl = "https://www.sevaafoundation.com/seva-backend";
const defaultImage = "https://www.sevaafoundation.com/og-blogs.jpg";

type Blog = {
  id: number;
  title: string;
  category?: string;
  short_description?: string;
  content: string;
  author_name?: string;
  created_at?: string;
  published_at?: string;
  cover_image?: string;
  status?: string;
  generation_source?: string;
  seo_title?: string;
  meta_description?: string;
};

const BlogPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const token = localStorage.getItem("sevaa_admin_token");

  useEffect(() => {
    let isMounted = true;

    fetch(`${backendUrl}/api/blogs/preview.php?id=${encodeURIComponent(id || "")}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || "Blog could not be loaded.");
        }
        return data;
      })
      .then((data) => {
        if (isMounted) setBlog(data.blog);
      })
      .catch((fetchError) => {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Blog could not be loaded.");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id, token]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-orange-600" size={36} />
      </div>
    );
  }

  if (!blog || error) {
    return <div className="p-8 text-center font-semibold text-red-600">{error || "Blog not found."}</div>;
  }

  const publishedDate = blog.published_at || blog.created_at;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 font-semibold text-slate-500 transition hover:text-orange-600"
      >
        <ArrowLeft size={18} /> Back to List
      </button>

      <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <img
          src={blog.cover_image || defaultImage}
          alt={`${blog.title} - SEVAA Foundation`}
          className="h-72 w-full object-cover md:h-96"
        />

        <div className="p-6 md:p-10">
          <div className="mb-5 flex flex-wrap gap-2">
            {blog.category && (
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-orange-700">
                {blog.category}
              </span>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black uppercase tracking-wider text-slate-600">
              {blog.status || "published"}
            </span>
            <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-teal-700">
              {blog.generation_source === "ai" ? "Automatic" : "Manual"}
            </span>
          </div>

          <h1 className="mb-5 text-3xl font-black leading-tight text-slate-950 md:text-5xl">{blog.title}</h1>

          <div className="mb-8 flex flex-wrap items-center gap-4 border-b border-slate-100 pb-6 text-sm font-semibold text-slate-500">
            <span className="flex items-center gap-2">
              <User size={16} /> {blog.author_name || "SEVAA Foundation"}
            </span>
            {publishedDate && (
              <span className="flex items-center gap-2">
                <Calendar size={16} /> {new Date(publishedDate).toLocaleDateString("en-IN")}
              </span>
            )}
          </div>

          {blog.short_description && (
            <p className="mb-9 rounded-2xl border border-orange-100 bg-orange-50/70 p-5 text-lg font-medium leading-8 text-slate-700">
              {blog.short_description}
            </p>
          )}

          <BlogContent content={blog.content} compact />

          <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">SEO Preview Data</p>
            <p className="mt-3 font-bold text-blue-700">{blog.seo_title || blog.title}</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {blog.meta_description || blog.short_description}
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPreview;
